import { useEffect, useMemo } from 'react';

import { Flag02Icon } from '@hugeicons-pro/core-solid-rounded';
import {
  Download01Icon,
  FileSecurityIcon,
  Pdf01Icon,
  User02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { meetsLogic } from 'constants/logic';
import { getAnswerValue } from 'helpers/form';
import { shouldHideCustomReferenceAiSummary } from 'helpers/reference-ai-summary';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ActionsMenu } from 'components/ActionsMenu/ActionsMenu';
import { FormResponses } from 'components/Form/FormResponses/FormResponses';
import { Button } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';
import { SummaryStat } from 'components/SummaryStat/SummaryStat';

import { useGetAnswersQuery } from 'api/candidate-checks-api';
import { selectCompanyChecks } from 'api/company-checks-slice';

import { Statuses, VerificationCard } from './../VerificationCard/VerificationCard';

export const CombinedReferees = ({
  candidate,
  referees,
  check,
  brand = {},
  canAccessResults = true,
  setLoading = () => {},
  getPdfReport = () => {},
  getCustomPdfReport = () => {},
}) => {
  const { data: response, isLoading } = useGetAnswersQuery({ id: check.id });
  const { t } = useTranslation();
  const companyChecks = useSelector(selectCompanyChecks);

  const isUsingCustomReport = useMemo(() => {
    return companyChecks.find(cc => cc.type === check.type)?.configuration?.in_custom_report;
  }, [companyChecks, check.type]);

  const showCustomReportMenu = isUsingCustomReport && check.type === 'custom_reference';

  const noAiSummary = useMemo(
    () =>
      shouldHideCustomReferenceAiSummary({
        checkType: check?.type,
        brand,
        companyChecks,
      }),
    [check?.type, brand?.ff_ai_reference_summary, companyChecks],
  );
  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  const fields = useMemo(() => {
    if (!response?.result) return;

    // Set data
    const {
      form: { fields, logic_jumps },
      answers,
      flag_severities = {},
    } = response.result;

    // Get fields with logic (maxOrder and existingOrders used when logic.to === -1 "finish form")
    const maxOrder = fields.length ? Math.max(...fields.map(f => f.order)) : 0;
    const existingOrdersSet = new Set(fields.map(f => Number(f.order)));
    let hasSubField = {};
    let ignoredFields = [];
    logic_jumps.forEach(logic => {
      hasSubField[logic.from] = !!hasSubField[logic.from]
        ? [...hasSubField[logic.from], logic]
        : [logic];
      const toNum = Number(logic.to);
      if (toNum === -1) {
        for (let i = logic.from + 1; i <= maxOrder; i++) {
          ignoredFields.push(i);
        }
      } else {
        for (let i = logic.from + 1; i < logic.to; i++) {
          ignoredFields.push(i);
        }
      }
    });

    // Filter ignored fields
    let data = fields.filter(f => !ignoredFields.includes(f.order));

    // Build data array
    return data.map(f => {
      // Copy object
      let field = { ...f };

      // logic for sub-fields
      let logics = Object.keys(hasSubField).includes(`${field.order}`)
        ? hasSubField[field.order]
        : [];

      // Set field answers
      field.answers = [];
      referees.forEach(referee => {
        // Add orders that should be add as sub-fields
        let subFieldsOrders = [];
        logics.forEach(logic => {
          const fieldToEval = fields.find(f => f.order === logic.field);
          const valueToEval = getAnswerValue(answers[referee.id][fieldToEval?.id]);
          const toNum = Number(logic.to);
          const endOrder = toNum === -1 ? maxOrder : toNum - 1;
          const startOrder = logic.from + 1;
          if (meetsLogic(logic, valueToEval)) {
            for (let i = startOrder; i <= endOrder; i++) {
              subFieldsOrders = subFieldsOrders.filter(order => order !== i);
            }
          } else {
            for (let i = startOrder; i <= endOrder; i++) {
              if (existingOrdersSet.has(i) && !subFieldsOrders.includes(i)) {
                subFieldsOrders.push(i);
              }
            }
          }
        });

        // Add sub-fields
        let subFields = [];
        subFieldsOrders.forEach(order => {
          const field = fields.find(f => f.order == order);
          const rawAnswer = answers[referee.id][field.id];
          const value = getAnswerValue(rawAnswer);
          subFields.push({
            ...field,
            answer: value,
            translated_text: rawAnswer?.translated_text,
            language_code: rawAnswer?.language_code,
            translation_score: rawAnswer?.translation_score,
          });
        });

        // Add referee answer to field answers
        const rawAnswer = answers[referee.id][field.id];
        field.answers.push({
          name: referee.name,
          answer: getAnswerValue(rawAnswer),
          translated_text: rawAnswer?.translated_text,
          language_code: rawAnswer?.language_code,
          translation_score: rawAnswer?.translation_score,
          subFields,
          flagSeverity: flag_severities[referee.id]?.[field.id],
        });
      });

      return field;
    });
  }, [response, referees]);

  const summaryStats = useMemo(() => {
    const result = response?.result;
    const form = result?.form;
    const flagSeverities = result?.flag_severities || {};
    const bySeverity = severities =>
      (severities || []).reduce((acc, val) => {
        acc[val] = (acc[val] || 0) + 1;
        return acc;
      }, {});
    const refereeStats = (referees || []).map(referee => {
      const severities = Object.values(flagSeverities[referee.id] || {});
      const bySev = bySeverity(severities);
      return {
        name: referee.name,
        submittedAt: referee?.submitted_at ?? null,
        totalFlagged: severities.length,
        high: bySev.high ?? 0,
        medium: bySev.medium ?? 0,
        low: bySev.low ?? 0,
      };
    });
    const candidateSeverities = Object.values(flagSeverities.candidate || {});
    const candidateBySev = bySeverity(candidateSeverities);
    return {
      candidateSubmittedAt: check?.submitted_at ?? null,
      refereeStats,
      candidate: {
        totalFlagged: candidateSeverities.length,
        high: candidateBySev.high ?? 0,
        medium: candidateBySev.medium ?? 0,
        low: candidateBySev.low ?? 0,
      },
    };
  }, [response, referees, check?.submitted_at]);

  return (
    <div className="border border-neutral-4 rounded-3xl shadow-sm bg-neutral-1 w-1/2 overflow-hidden">
      <div className="p-8 flex flex-col gap-8 border-b border-neutral-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <IconComponent icon={User02Icon} />
            <div className="flex flex-col gap-1">
              <p className="text-lg font-medium text-text-tertiary uppercase tracking-wide">
                Referees
              </p>
              <h1 className="text-4xl font-medium tracking-tight text-neutral-7">
                {(referees || []).map(r => r.name).join(' & ')}
              </h1>
            </div>
          </div>
          {canAccessResults &&
            (showCustomReportMenu ? (
              <ActionsMenu
                newIconLibrary
                small
                triggerIcon={Download01Icon}
                className="[&_svg]:text-neutral-6 [&_svg]:cursor-pointer [&_svg]:stroke-[1.5]">
                <ActionsMenu.Item
                  icon={Pdf01Icon}
                  title="Download Report"
                  onClick={() => getPdfReport(check)}
                  disabled={check?.ai_summary_pending}
                  newIconLibrary
                />
                <ActionsMenu.Item
                  icon={FileSecurityIcon}
                  title="Download Custom Report"
                  onClick={() => getCustomPdfReport(check)}
                  newIconLibrary
                />
              </ActionsMenu>
            ) : (
              <Button
                type="secondary"
                small
                light
                icon="download"
                tip={
                  check?.ai_summary_pending
                    ? t('check.actions.aiSummaryPending')
                    : t('common.download')
                }
                dataTestId="combined-report-download-button"
                onClick={() => getPdfReport(check)}>
                Download
              </Button>
            ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-6">
          <SummaryStat
            label="Candidate submitted"
            value={
              summaryStats.candidateSubmittedAt
                ? moment(summaryStats.candidateSubmittedAt).format('MMMM D, YYYY')
                : '—'
            }
          />
          {(summaryStats.refereeStats || []).map((refereeStat, i) => (
            <SummaryStat
              key={i}
              label={`${refereeStat.name} submitted`}
              title={`${refereeStat.name} submitted`}
              value={
                refereeStat.submittedAt
                  ? moment(refereeStat.submittedAt).format('MMMM D, YYYY')
                  : '—'
              }
            />
          ))}
          <SummaryStat
            label="Candidate flagged"
            value={
              summaryStats.candidate.totalFlagged > 0 ? summaryStats.candidate.totalFlagged : '-'
            }
            tooltip="High, medium and low risk flagged response counts (candidate form)"
            {...(() => {
              const items = [
                {
                  count: summaryStats.candidate.high,
                  className: 'text-red-2',
                  tooltip: 'High risk',
                },
                {
                  count: summaryStats.candidate.medium,
                  className: 'text-orange-2',
                  tooltip: 'Medium risk',
                },
                {
                  count: summaryStats.candidate.low,
                  className: 'text-blue-3',
                  tooltip: 'Low risk',
                },
              ].filter(item => item.count > 0);
              return items.length > 0
                ? {
                    breakdown: items,
                    breakdownIcon: <HugeiconsIcon icon={Flag02Icon} size={20} />,
                  }
                : {};
            })()}
          />
          {(summaryStats.refereeStats || []).map((refereeStat, i) => {
            const items = [
              { count: refereeStat.high, className: 'text-red-2', tooltip: 'High risk' },
              { count: refereeStat.medium, className: 'text-orange-2', tooltip: 'Medium risk' },
              { count: refereeStat.low, className: 'text-blue-3', tooltip: 'Low risk' },
            ].filter(item => item.count > 0);
            return (
              <SummaryStat
                key={i}
                label={`${refereeStat.name} flagged`}
                value={refereeStat.totalFlagged > 0 ? refereeStat.totalFlagged : '-'}
                title={`${refereeStat.name} flagged responses`}
                tooltip="High, medium and low risk flagged response counts"
                {...(items.length > 0
                  ? {
                      breakdown: items,
                      breakdownIcon: <HugeiconsIcon icon={Flag02Icon} size={20} />,
                    }
                  : {})}
              />
            );
          })}
        </div>
      </div>
      <Header candidate={candidate} referees={referees} />
      {response && (
        <FormResponses
          form={response.result.form}
          fields={fields}
          aiSummary={check?.ai_summary}
          noAiSummary={noAiSummary}
        />
      )}
    </div>
  );
};

const Header = ({ candidate, referees }) => (
  <div className="flex gap-8 bg-slate-50 pt-8 px-8">
    <VerificationCard>
      <VerificationCard.Header icon="mail">email address</VerificationCard.Header>
      {referees.map((referee, i) => {
        let emailVerification;

        if (referee.email === candidate.email)
          emailVerification = { status: Statuses.Error, msg: 'Email addresses match' };
        else if (referee.has_company_email)
          emailVerification = { status: Statuses.Success, msg: 'Registered Company Email' };
        else emailVerification = { status: Statuses.Warn, msg: 'Personal email used' };

        return (
          <div
            key={referee.id}
            className={cn('u-padding-y--small', { ['u-divider-top-border']: i !== 0 })}>
            <VerificationCard.Link href={`mailto:${referee.email}`}>
              {referee.email}
            </VerificationCard.Link>
            <VerificationCard.AlertMessage status={emailVerification.status}>
              {emailVerification.msg}
            </VerificationCard.AlertMessage>
          </div>
        );
      })}
    </VerificationCard>
    <VerificationCard>
      <VerificationCard.Header icon="linkedin" fa>
        linkedin profile
      </VerificationCard.Header>
      {referees.map((referee, i) => {
        const linkedinVerification = referee.linkedin_profile
          ? { status: Statuses.Success, msg: 'Verified Linkedin' }
          : { status: Statuses.Warn, msg: 'No information provided' };

        return (
          <div
            key={referee.id}
            className={cn('u-padding-y--small', { ['u-divider-top-border']: i !== 0 })}>
            <VerificationCard.Value>
              {referee.linkedin_profile ? referee.linkedin_profile : '-'}
            </VerificationCard.Value>
            <VerificationCard.AlertMessage status={linkedinVerification.status}>
              {linkedinVerification.msg}
            </VerificationCard.AlertMessage>
          </div>
        );
      })}
    </VerificationCard>
    <VerificationCard>
      <VerificationCard.Header icon="shield">ip address</VerificationCard.Header>
      {referees.map((referee, i) => {
        const ipVerification =
          referee.ip === candidate.ip
            ? { status: Statuses.Error, msg: 'IP address matches candidate' }
            : { status: Statuses.Success, msg: 'Unique IP Address' };

        return (
          <div
            key={referee.id}
            className={cn('u-padding-y--small', { ['u-divider-top-border']: i !== 0 })}>
            <VerificationCard.Value>{referee.ip}</VerificationCard.Value>
            <VerificationCard.AlertMessage status={ipVerification.status}>
              {ipVerification.msg}
            </VerificationCard.AlertMessage>
          </div>
        );
      })}
    </VerificationCard>
  </div>
);
